"use client";

import "../src/common/assets/styles/custom.scss";
import { useEffect, useState } from "react";
import {
  BrowserRouter as Router,
  Routes,
  Route,
  useLocation,
  Navigate,
} from "react-router-dom";
import ScrollToTop from "@common/components/ScrollToTop";
import Header from "./common/components/Header";
import Footer from "./common/components/Footer";
import Home from "./pages/Home/view";
import Contact from "./pages/Contact/view";
import OurJourney from "./pages/OurJourney/view";
import PageLoader from "./pages/Home/view/components/PageLoader";
import Collections from "./pages/Collection/view";
import CollectionDetail from "./pages/CollectionDetail/view";
import SkuDetail from "./pages/SkuDetail/view";
import Faq from "./pages/Faq/view";
import PrivacyPolicy from "./pages/PrivacyPolicy/view";
import WhereToBuy from "@pages/WhereToBuy/view";
import Sustainability from "./pages/Sustainability/view";
import TermsAndConditions from "./pages/TermsAndConditions/view";
import QualityStandards from "@pages/QualityStandards/view";
import Search from "@pages/Search/view";
import InspiredLiving from "@pages/InspiredLiving/view";
import BlogListing from "@pages/InspiredLiving/view/components/BlogListing";
import BlogDetails from "@pages/BlogDetails/view";
import ProductListing from "@pages/ProductListing/view";
import Shop from "@pages/Shop/view";
import ProductDetails from "@pages/ProductDetails/view";
import Cart from "@pages/Cart/view";
import CartDetails from "@pages/CartDetails/view";
import CartPayment from "@pages/CartPayment/view";
import PaymentSuccess from "@pages/PaymentSuccess/view";
import PaymentFailure from "@pages/PaymentFailure/view";
import Profile from "@pages/Profile/view";
import Addressess from "@pages/Adressess/view";
import MyWishlist from "@pages/MyWishlist/view";
import MyOrders from "@pages/MyOrders/view";
import Toaster from "@common/components/Toaster";
import { Provider } from "react-redux";
import { store } from "./store";
import { CartProvider } from "./contexts/CartContext";
import {
  fetchGuestToken,
  getAuthToken,
  isUserLoggedIn,
} from "@common/utils/auth/authService";
import ResetPassword from "@pages/ResetPassword/view";
import CurtainsTemplate from "@pages/CurtainsTemplate/view";
import SeoContainer from "@common/components/SeoContainer";
import { getSeoData } from "@common/utils/common";
import { defaultSEO } from "@common/utils/constant";
import WishlistInitializer from "./components/WishlistInitializer";
import { initializePreloader } from "./utils/preloader";
import { initPerformanceMonitoring } from "./utils/performanceMonitor";
import Csr from "@pages/Csr/view";
import Trade from "@pages/Trade/view";
import Careers from "@pages/Careers/view";
import JobDetail from "@pages/Careers/JobDetail";

const MainContainer = ({ children, showBand }) => {
  const location = useLocation();
  const getClassForRoute = () => {
    return location.pathname.includes("collections")
      ? "lyt-content__tpl-collection"
      : location.pathname.includes("plp") || location.pathname.includes("pdp")
        ? "lyt-content__plp"
        : "";
  };

  return (
    <div
      className={`lyt-content ${showBand ? "lyt-content--typ-band" : ""
        } ${getClassForRoute()}`}
    >
      {children}
    </div>
  );
};

const ProtectedRoute = ({ children }) => {
  const token = isUserLoggedIn();
  if (!token) return <Navigate to="/" replace />;
  return children;
};

function AppRouter() {
  const [showBand, setShowBand] = useState(false);
  const location = useLocation();
  const [isLoading, setIsLoading] = useState(true);
  const [isAppReady, setIsAppReady] = useState(false);
  const [seoData, setSeoData] = useState(null);

  useEffect(() => {
    setTimeout(() => {
      setIsLoading(false);
    }, 2000);
  }, []);

  useEffect(() => {
    const initializeApp = async () => {
      try {
        let token = getAuthToken();
        if (!token) token = await fetchGuestToken();
        // setIsLoading(false);
      } catch (err) {
        console.error("App init error:", err);
      }
    };

    const fetchSeo = async () => {
      try {
        const data = await getSeoData(location.pathname);
        if (Object.keys(data).length === 0) {
          setSeoData(defaultSEO);
        } else {
          // Step 1: Parse the JSON string
          const parsedArray = JSON.parse(data?.imagesAltText || "[]");

          // Step 2: Convert to desired object shape
          const resultObj = parsedArray.reduce((acc, item) => {
            acc[item.imageName] = item.imageAlt;
            return acc;
          }, {});

          console.log("imageSeoAlt resultObj", resultObj);
          localStorage.setItem("imageSeoAlt", JSON.stringify(resultObj));

          setSeoData(data);
        }
      } catch (err) {
        // console.error('SEO fetch error:', err);
        setSeoData(defaultSEO);
      }
    };

    // initializeApp();
    const startApp = async () => {
      await initializeApp();
      await fetchSeo();

      // Initialize preloader for cart resources
      initializePreloader();

      // Initialize performance monitoring (development only)
      initPerformanceMonitoring();

      setIsAppReady(true);
    };

    startApp();
  }, [location.pathname]);

  if (!isAppReady) return <PageLoader />;

  const SHOP_URL = "https://shop.sansaar.co.in/collections/all-bedding";

  const ExternalRedirect = ({ to }) => {
    if (to.includes("products/bedsheet-set")) {
      window.location.href = "https://shop.sansaar.co.in/collections/all-bedding?filter.p.product_type=BEDDING+SET";
    } else {
      window.location.href = to;
    }
    // useEffect(() => {
    //   window.location.replace(to);
    // }, [to]);

    return null; // or a loading spinner
  };

  return (
    <Provider store={store}>
      <CartProvider>
        <WishlistInitializer />
        <ScrollToTop />
        <div>
          <Header isBand={showBand} />
          <SeoContainer {...seoData} />
          <main>
            <MainContainer showBand={showBand}>
              <Routes>
                <Route path="/" element={<Home />} />
                <Route path="/franchise-enquiry" element={<Contact />} />
                <Route path="/our-journey" element={<OurJourney />} />
                <Route path="/collections" element={<Collections />} />
                <Route
                  path="/collections/:filterType/:filterValue"
                  element={<Collections />}
                />
                <Route
                  path="/collection-detail/:slug"
                  element={<CollectionDetail />}
                />
                <Route path="/sku-detail/:slug" element={<SkuDetail />} />
                <Route path="/faq" element={<Faq />} />
                <Route path="/privacy-policy" element={<PrivacyPolicy />} />
                <Route path="/where-to-buy" element={<WhereToBuy />} />
                <Route path="/sustainability" element={<Sustainability />} />
                <Route
                  path="/quality-standards"
                  element={<QualityStandards />}
                />
                <Route path="/tnc" element={<TermsAndConditions />} />
                <Route path="/search" element={<Search />} />
                <Route path="/inspired-living" element={<InspiredLiving />} />
                <Route path="/blogs" element={<BlogListing />} />
                <Route path="/blog/:slug" element={<BlogDetails />} />
                <Route path="/products" element={<ExternalRedirect to={SHOP_URL} />} />
                <Route
                  path="/products/collection/:collection"
                  element={<ExternalRedirect to={SHOP_URL} />}
                />
                <Route
                  path="/products/packagingtype/:packagingtype"
                  element={<ExternalRedirect to={SHOP_URL} />}
                />
                <Route
                  path="/products/design/:design"
                  element={<ExternalRedirect to={SHOP_URL} />}
                />
                <Route
                  path="/products/fabric/:fabric"
                  element={<ExternalRedirect to={SHOP_URL} />}
                />
                <Route
                  path="/products/size/:size"
                  element={<ExternalRedirect to={SHOP_URL} />}
                />
                <Route
                  path="/products/thread-count/:threadcount"
                  element={<ExternalRedirect to={SHOP_URL} />}
                />
                <Route
                  path="/products/:category"
                  element={<ExternalRedirect to={SHOP_URL} />}
                />
                <Route
                  path="/shop"
                  element={<ExternalRedirect to={SHOP_URL} />}
                />
                <Route
                  path="/product-description/:slug"
                  element={<ExternalRedirect to={SHOP_URL} />}
                />
                <Route path="/add-to-cart" element={<ExternalRedirect to={SHOP_URL} />} />
                <Route path="/address" element={<ExternalRedirect to={SHOP_URL} />} />
                <Route path="/initiate-checkout" element={<ExternalRedirect to={SHOP_URL} />} />
                <Route path="/payment-success" element={<ExternalRedirect to={SHOP_URL} />} />
                <Route path="/payment-failure" element={<ExternalRedirect to={SHOP_URL} />} />

                <Route path="/curtain" element={<CurtainsTemplate />} />
                <Route path="/upholstery" element={<CurtainsTemplate />} />
                <Route path="/csr" element={<Csr />} />
                <Route path="/trade" element={<Trade />} />
                <Route path="/careers" element={<Careers />} />
                <Route path="/careers/:id" element={<JobDetail />} />
                <Route path="/multipurpose" element={<CurtainsTemplate />} />
                {/* Protected Routes */}
                <Route
                  path="/profile"
                  element={
                    <ProtectedRoute>
                      <Profile />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/my-addresses"
                  element={
                    <ProtectedRoute>
                      <Addressess />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/my-orders"
                  element={
                    <ProtectedRoute>
                      <MyOrders />
                    </ProtectedRoute>
                  }
                />
                <Route
                  path="/my-wishlist"
                  element={
                    <ProtectedRoute>
                      <MyWishlist />
                    </ProtectedRoute>
                  }
                />
                <Route path="/reset-password" element={<ResetPassword />} />
              </Routes>
            </MainContainer>
          </main>
          <Footer />
          <Toaster />
        </div>
      </CartProvider>
    </Provider>
  );
}

function App() {
  return (
    <Router future={{ v7_startTransition: true, v7_relativeSplatPath: true }}>
      <AppRouter />
    </Router>
  );
}

export default App;
